import {
  Component,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { QueryBase, QueryObjectsModel } from '@barthauer/abc/core/data';
import { AbcContainerService, _AbcGridColumn, _AbcGridComponent } from '@barthauer/abc/ui-components';
import { MasterDetailTemplateData } from 'devextreme/ui/data_grid';

@Component({
  selector: 'abc-opath-queries-editor-nested-grid',
  templateUrl: './nested-grid.component.html',
  styleUrls: ['./nested-grid.component.scss'],
})
export class NestedGridComponent implements OnInit, OnChanges {
  @Input() data: any;
  @Input() query: QueryObjectsModel;
  @Input() set level(value: number) {
    this._level = value + 1;
  }
  @ViewChildren('grid') grid: QueryList<_AbcGridComponent>;
  @ViewChildren('flatGrid') flatGrid: QueryList<_AbcGridComponent>;

  protected _level: number = 0;
  public tabs: string[];
  public subQueries: any[] = [];
  public gridColumns: Map<string, _AbcGridColumn[]> = new Map();

  private viewRef: ViewContainerRef;

  constructor(private abcContainer: AbcContainerService) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['data']) {
      await this.createTabs();
    }
  }

  async ngOnInit(): Promise<void> {
    await this.createTabs();
  }
  public isEmpty(data: Object[]): boolean {
    if (data.length === 0) {
      return true;
    }
    for (const element of data) {
      if (Object.keys(element).length !== 0) {
        return false;
      }
    }
    return true;
  }

  public refreshNestedGrid(): void {
    this.grid.forEach((grid: _AbcGridComponent) => {
      grid.instance.updateDimensions();
    });

    this.flatGrid.forEach((flatGrid: _AbcGridComponent) => {
      flatGrid.instance.updateDimensions();
    });
  }

  public setMasterDetailTemplateData = (tdElement: HTMLElement, rowOption: MasterDetailTemplateData) => {
    const nested = this.abcContainer.createComponent(this.viewRef, NestedGridComponent);
    const nestedInstance = <NestedGridComponent>nested.componentRef?.instance;
    const nestedDom = nested.domElement;
    if (nestedInstance) {
      nestedInstance.level = this._level;
      nestedInstance.data = rowOption?.data;
      tdElement.appendChild(nestedDom);
    }
  };

  private async createTabs(): Promise<void> {
    this.tabs = [];
    this.subQueries = [];
    this.gridColumns = new Map();

    let headers = [];

    headers = Object.keys(this.data);
    for (let header of headers) {
      if (Array.isArray(this.data[header]) && (headers.length === 1 || !this.isEmpty(this.data[header]))) {
        this.tabs.push(header);
      }
    }

    for (let tab of this.tabs) {
      await this.buildDataStructure(tab, this.data[tab]);
    }
  }

  private async buildDataStructure(tab: string, data: any): Promise<void> {
    this.buildSubQueries(data, tab);
    this.buildGridColumns(data, tab);
  }

  private buildSubQueries(data: any, tab: string) {
    for (let row of data) {
      let keys = Object.keys(row);
      if (keys) {
        for (let key of keys) {
          if (row[key] && ((Array.isArray(row[key]) && row[key].length) || typeof row[key] === 'object')) {
            if (!(this.subQueries[tab] && this.subQueries[tab].hasOwnProperty(key))) {
              this.subQueries[tab] = { ...this.subQueries[tab], [key]: true };
            }
            if (row[key] && typeof row[key] === 'object' && !Array.isArray(row[key])) {
              row[key] = [row[key]];
            }
          }
        }
      }
    }
  }

  private buildGridColumns(data: any, tab: string) {
    let columns = this.gridColumns.get(tab);
    if (!columns) {
      columns = [];
      this.gridColumns.set(tab, columns);
    }
    const joinedQueries = this.getJoinedQueries(tab, this.query?.ObjectQueries);
    if (joinedQueries) {
      for (const q of joinedQueries) {
        for (const c of q.Columns) {
          if (!c.IsHidden && !c.IsFilter) {
            const name = c.Name ?? c.OPath;
            if (!columns.some((column) => column.caption === name)) {
              const newcolumn: _AbcGridColumn = { caption: name, dataField: name, name: name };
              columns.push(newcolumn);
            }
          }
        }
      }
    }
    if (data.length) {
      let keys = Object.keys(data[0]);

      for (let key of keys) {
        if (!Array.isArray(data[0][key]) && typeof data[0][key] !== 'object') {
          if (!columns.some((column) => column.caption === key)) {
            const newcolumn: _AbcGridColumn = { caption: key, dataField: key, name: key };
            columns.push(newcolumn);
          }
        }
      }
    }
  }

  private getJoinedQueries(tab: string, candidates: QueryBase[]): QueryBase[] {
    const result: QueryBase[] = [];
    if (candidates) {
      for (const q of candidates) {
        if (q.Name === tab || q.Name === null) {
          result.push(q);
          result.push(...this.getJoinedQueries(undefined, q.ObjectQueries));
        }
      }
    }
    return result;
  }
}
